import React, { useEffect, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";

interface RunHistoryFilters {
  status: string;
}

interface RunHistoryFilterProps {
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  statusOptions: { value: string; label: string }[];
}

export const RunHistoryFilter: React.FC<RunHistoryFilterProps> = ({
  statusFilter,
  onStatusFilterChange,
  onApplyFilters,
  onResetFilters,
  statusOptions,
}) => {
  // Transform statusOptions to options format
  const statusSelectOptions = useMemo(
    () =>
      statusOptions?.map((status) => ({
        value: status.value,
        label: status.label,
      })) || [],
    [statusOptions]
  );

  // Define filter fields
  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "status",
        label: "Status",
        type: "select",
        placeholder: "All Status",
        options: statusSelectOptions,
        className: "col-span-1",
        classes: {
          container: "w-full",
        },
      },
    ],
    [statusSelectOptions]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Sync form values when statusFilter prop changes
  useEffect(() => {
    form.reset({
      status: statusFilter || "",
    });
  }, [statusFilter, form]);

  const handleFilterSubmit = () => {
    const formValues = form.getValues();
    onApplyFilters();
  };

  const handleFilterReset = () => {
    form.reset({
      status: "",
    });
    onResetFilters();
  };

  const handleFieldChange = (field: string, value: string) => {
    if (field === "status") {
      onStatusFilterChange(value);
    }
  };

  return (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        onFieldChange={handleFieldChange}
        gridColumns={1}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            buttonContainer: "flex justify-end gap-2 mt-4",
          },
        }}
      />
    </div>
  );
};
